import React, { useState, useMemo } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import Card from '../Card';

export type Plan = {
  name: string;
  id: string;
};

type ConfigureCbsContentPackagesModalProps = {
  handleCancel: () => void;
  handleChange: (selected: boolean[]) => void;
  open: boolean;
  defaultValue: boolean[];
  plans: Plan[] | ((selected: boolean[]) => Plan[]);
  requiredSelections: number;
  includedLabels?: string[];
};

const ConfigureCbsContentPackagesModal = ({
  open,
  plans: propPlans,
  defaultValue,
  handleCancel,
  handleChange,
  requiredSelections,
  includedLabels = [],
}: ConfigureCbsContentPackagesModalProps) => {
  const [selected, setSelected] = useState(defaultValue);
  const [searchQuery, setSearchQuery] = useState('');

  const plansToRender =
    typeof propPlans === 'function' ? propPlans(selected) : propPlans;

  const title = 'Choose libraries';

  const { selectedCount, remainingSelections, filteredPlans } = useMemo(() => {
    const selectedCount = selected.filter(Boolean).length;
    const filtered =
      plansToRender?.filter((plan) =>
        plan.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ) || [];
    return {
      selectedCount,
      remainingSelections: requiredSelections - selectedCount,
      filteredPlans: filtered,
    };
  }, [selected, requiredSelections, plansToRender, searchQuery]);

  const filteredIncludedLabels = useMemo(
    () =>
      includedLabels.filter((label) =>
        label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [includedLabels, searchQuery],
  );

  const onConfirm = () => {
    handleChange(selected);
  };

  function toggleCheck(planId: string) {
    setSelected((prevSelected) => {
      const newSelected = [...prevSelected];
      const index = plansToRender.findIndex((plan) => plan.id === planId);
      if (index !== -1) {
        newSelected[index] = !newSelected[index];
      }
      return newSelected;
    });
  }

  const noResults =
    filteredPlans.length === 0 && filteredIncludedLabels.length === 0;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      scroll={'paper'}
      PaperProps={{
        sx: {
          width: '100%',
          maxHeight: '560px!important',
          minWidth: '560px!important',
        },
      }}
    >
      <DialogTitle>
        <div className="flex flex-col">
          <span>{title}</span>
          <span className="text-sm font-normal text-gray-600 mt-1">
            Selected: {selectedCount} of {requiredSelections} packages
            {remainingSelections > 0 && (
              <span className="ml-2">
                ({remainingSelections} more required)
              </span>
            )}
          </span>
        </div>
      </DialogTitle>
      <DialogContent dividers={true}>
        <div className="mb-4">
          <TextField
            fullWidth
            placeholder="Search libraries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </div>
        {noResults ? (
          <div className="text-center text-gray-500 py-4">
            No libraries found matching your search
          </div>
        ) : (
          <>
            {filteredIncludedLabels.map((label) => (
              <div className="w-full mb-2" key={label}>
                <Card dynamic>
                  <div className="flex flex-row items-center">
                    <Checkbox
                      disabled
                      checked
                      size="small"
                      sx={{
                        p: 0,
                        color: 'grey.400',
                        '&.Mui-checked.Mui-disabled': { color: 'grey.400' },
                      }}
                    />
                    <div className="font-medium text-sm ml-2 flex items-center">
                      {label}
                      <Chip
                        label="Included"
                        size="small"
                        sx={{
                          ml: 1,
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: '#e0e7ff',
                          color: '#1e3a8a',
                          borderRadius: '999px',
                          '& .MuiChip-label': { px: 1.25 },
                        }}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            ))}
            {filteredPlans.map((plan) => {
              const index = plansToRender.findIndex((p) => p.id === plan.id);
              return (
                <div className="w-full mb-2" key={plan.id}>
                  <Card dynamic>
                    <div className="flex flex-row items-center">
                      <Checkbox
                        checked={selected[index]}
                        size="small"
                        sx={{
                          p: 0,
                          '&.Mui-checked': { color: '#2459ed' },
                        }}
                        onChange={() => toggleCheck(plan.id)}
                      />
                      <div className="font-medium text-sm ml-2">
                        {plan.name}
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={selectedCount !== requiredSelections}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigureCbsContentPackagesModal;
